// A saved model = everything needed to restore an object to the editor and,
// later, place it on a floor plan: its voxels, the canvas (work-volume) size,
// and the real-world voxel scale. Persisted in the browser (collection-store)
// and round-trips to JSON for export/import.
//
// FORMAT VERSIONS
//  - v1 (legacy): voxels as a dense flat `cells: [ix,iy,iz,palette,...]` array.
//    A solid 256³ block is ~67M numbers — pathological to store/decode.
//  - v2 (current): voxels as per-chunk RLE (`voxels.chunks`), losslessly
//    compressed and bulk-decodable, PLUS an optional precomputed render asset
//    (`baked`) so Layout placement is a deserialize, not a re-mesh. The baked
//    asset is a regenerable cache — absent/stale is always safe (the placement
//    path falls back to building it on the fly).
// Old files keep loading (migrate, never reject); a v1 model is rewritten as v2
// on its next save.

import type { LengthUnit } from "./units";
import {
  CELL_SIZE,
  CHUNK_AREA,
  CHUNK_SIZE,
  CHUNK_VOLUME,
  type PaletteIndex,
  VoxelGrid,
  parseChunkKey,
} from "./voxel-grid";

// A saved top-down reference picture: the image (data URL) + its overlay
// transform. Stored in the browser with the model; stripped from JSON export.
export interface ReferenceData {
  src: string; // image data URL
  baseW: number; // plane geometry size (encodes aspect + base fit)
  baseH: number;
  p: number[]; // group position [x, y, z] (in the frame's local space)
  q: number[]; // group quaternion [x, y, z, w]
  s: number; // uniform scale
  opacity: number;
  // The view-face frame: which side of the volume the image sits on + its
  // orientation. Absent on legacy (top-down only) references → identity frame.
  framePos?: number[];
  frameQuat?: number[];
}

// ── v2 voxel storage: per-chunk run-length encoding ────────────────────────
// One FLAT number array, not an array of per-chunk objects: a record's voxels
// are deserialized by listModels() (the dock + the save name-collision check),
// and structured-cloning ~4096 little {key,rle} objects per model was the save
// freeze (seconds across a library). A single packed array clones ~100x faster.
// Layout, repeated per occupied chunk:
//   key, runCount, value, count, value, count, …   (runCount run-pairs follow)
// where value/count are RLE over the chunk's 4096-cell linear storage (order
// lx*256 + ly*16 + lz); a solid chunk → `key, 1, palette, 4096`. Plain numbers,
// so JSON export stays base64-free.
export interface SavedVoxels {
  data: number[];
}
// The pre-flatten shape, kept only to read/migrate older records.
interface LegacyVoxels {
  chunks: { key: number; rle: number[] }[];
}

// ── v2 baked render asset (derived; regenerated off-thread on save) ────────
// Geometry buffers for a placeable mesh, so a placement skips meshing entirely.
// Faces are merged per palette across all chunks. Lives only in IndexedDB
// (structured clone keeps typed arrays); stripped from JSON export.
export interface BakedFace {
  palette: number;
  pos: Float32Array;
  norm: Float32Array;
  idx: Uint32Array;
}
export interface BakedShell {
  faces: BakedFace[];
  gridLines: Float32Array;
  edgeLines: Float32Array;
}
// AO carries per-vertex (palette index, shade) rather than a baked color, so the
// off-thread bake needs no color management; the final color is
// `palette.color(pal) * shade`, applied at deserialize with the model's palette.
export interface BakedAO {
  pos: Float32Array;
  norm: Float32Array;
  pal: Uint8Array;
  shade: Float32Array;
}
export interface CellAABB {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}
// The baked render asset, sans any revision stamp. Persisted in the SEPARATE
// bake store (model/bake-store.ts), not in the model record — so listing models
// stays light. Regenerable from voxels at any time.
export interface BakedAssetCore {
  shell: BakedShell;
  ao: BakedAO | null;
  // Voxel AABB (cells). The deserialize path recomputes the SAME centering
  // translation + per-axis scale buildBaked does, from this + the model's
  // bounds/realIn — so there's one copy of that math and no frozen transform to
  // drift. null = empty grid.
  cellBounds: CellAABB | null;
}

// Fields common to every version.
interface SavedModelCommon {
  id: string;
  name: string;
  // The palette this model was painted with, indexed like the live table
  // (index 0 reserved). Snapshotted on save so the model keeps its own colors
  // regardless of later swatch edits in other documents. Absent on legacy
  // files → the loader falls back to the factory defaults.
  paletteColors?: number[];
  // Work-volume extents (gridGroup-local metres) — the canvas proportions.
  bounds: {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
    height: number;
  };
  // Voxel → real-world scale + the unit measurements display in.
  scale: { inchesPerCell: number; unit: LengthUnit };
  // Exact real canvas dimensions (inches) — placements scale to this precise
  // size, not the voxel-rounded one.
  realIn: { w: number; d: number; h: number };
  thumbnail: string; // PNG data URL
  // Optional reference picture (browser-only; stripped from JSON export).
  reference?: ReferenceData;
  createdAt: number;
  updatedAt: number;
}

export interface SavedModelV1 extends SavedModelCommon {
  version: 1;
  // Sparse voxels as a flat [ix, iy, iz, palette, ...] array.
  cells: number[];
}
export interface SavedModelV2 extends SavedModelCommon {
  version: 2;
  voxels: SavedVoxels;
  // NOTE: the baked render asset lives in the separate bake store
  // (model/bake-store.ts), NOT here. Older records may still carry an inline
  // `baked` at runtime; it's ignored and migrated to the bake store.
}

export type SavedModel = SavedModelV1 | SavedModelV2;

// A v1 model has no `version: 2`; treat anything that isn't explicitly v2 as the
// legacy dense-cells shape.
export function isV2(m: SavedModel): m is SavedModelV2 {
  return m.version === 2;
}

// ── v1 dense codec (legacy read path only) ─────────────────────────────────
export function gridToCells(grid: VoxelGrid): number[] {
  const out: number[] = [];
  for (const [ix, iy, iz, p] of grid.entries()) out.push(ix, iy, iz, p);
  return out;
}

// Write a v1 model's voxels into `grid` (caller clears + wraps in
// suppressRecording).
export function cellsToGrid(cells: number[], grid: VoxelGrid): void {
  for (let i = 0; i + 3 < cells.length; i += 4) {
    grid.set(cells[i], cells[i + 1], cells[i + 2], cells[i + 3] as PaletteIndex);
  }
}

// ── v2 RLE codec (flat) ──────────────────────────────────────────────────────
// RLE operates on a chunk's raw 4096-byte storage in native order, so there is
// no (x,y,z) ↔ index transform — encode/decode just walk the array.

// Append one chunk (key + run-count + runs) to the flat data array.
function appendChunk(data: number[], key: number, cells: Uint8Array): void {
  data.push(key);
  const countAt = data.length;
  data.push(0); // run-count placeholder
  let runs = 0;
  const n = cells.length;
  let i = 0;
  while (i < n) {
    const v = cells[i];
    let j = i + 1;
    while (j < n && cells[j] === v) j++;
    data.push(v, j - i);
    runs++;
    i = j;
  }
  data[countAt] = runs;
}

// Expand `runs` run-pairs starting at `rle[0]` into a fresh chunk and install it.
function installRuns(grid: VoxelGrid, key: number, rle: ArrayLike<number>, at: number, runs: number): void {
  const cells = new Uint8Array(CHUNK_VOLUME);
  let count = 0;
  let pos = 0;
  let i = at;
  for (let r = 0; r < runs; r++) {
    const v = rle[i++];
    const len = rle[i++];
    if (v !== 0) {
      cells.fill(v, pos, pos + len);
      count += len;
    }
    pos += len;
  }
  if (count > 0) grid.setRawChunk(key, cells, count);
}

// Snapshot the grid's occupied chunks as a flat RLE array. Numbers only —
// JSON-friendly (no base64 on export) and cheap to structured-clone.
export function gridToVoxels(grid: VoxelGrid): SavedVoxels {
  const data: number[] = [];
  for (const [key, chunk] of grid.rawChunks()) appendChunk(data, key, chunk.cells);
  return { data };
}

// Bulk-restore voxels into `grid` (caller clears + wraps in suppressRecording,
// and must markAllChunksDirty after — setRawChunk does NOT dirty/bump version).
// Accepts the flat format AND the legacy per-chunk-object format (older records).
export function voxelsToGrid(voxels: SavedVoxels | LegacyVoxels, grid: VoxelGrid): void {
  if ("chunks" in voxels) {
    for (const { key, rle } of voxels.chunks) installRuns(grid, key, rle, 0, rle.length / 2);
    return;
  }
  const d = voxels.data;
  let i = 0;
  while (i < d.length) {
    const key = d[i++];
    const runs = d[i++];
    installRuns(grid, key, d, i, runs);
    i += runs * 2;
  }
}

// Convert a legacy per-chunk-object voxels blob to the flat format, without a
// grid round-trip — for the one-time record migration.
export function flattenLegacyVoxels(legacy: LegacyVoxels): SavedVoxels {
  const data: number[] = [];
  for (const { key, rle } of legacy.chunks) {
    data.push(key, rle.length / 2);
    for (const x of rle) data.push(x);
  }
  return { data };
}

// Convert a v1 dense `cells` array to flat v2 voxels (for the migration). A full
// 256³ block shrinks from ~67M numbers to a few thousand.
export function v1CellsToVoxels(cells: number[]): SavedVoxels {
  const grid = new VoxelGrid();
  cellsToGrid(cells, grid);
  return gridToVoxels(grid);
}

// Exact voxel AABB (cells) — for footprint centering at bake/deserialize.
// THREE-free, so the bake worker and model-bake share one copy. null = empty.
// Chunk-aware: a FULL chunk contributes its whole 16³ extent without scanning
// its 4096 cells, so a solid model is O(chunks), not O(volume) (a per-cell scan
// of a solid 256³ — 16.7M cells — was ~2.8s of every bake). Only partial
// (boundary) chunks are scanned cell-by-cell for their exact extent.
export function gridCellBounds(grid: VoxelGrid): CellAABB | null {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (const [ck, chunk] of grid.rawChunks()) {
    const [cx, cy, cz] = parseChunkKey(ck);
    const bx = cx * CHUNK_SIZE, by = cy * CHUNK_SIZE, bz = cz * CHUNK_SIZE;
    if (chunk.count === CHUNK_VOLUME) {
      if (bx < minX) minX = bx;
      if (by < minY) minY = by;
      if (bz < minZ) minZ = bz;
      if (bx + CHUNK_SIZE - 1 > maxX) maxX = bx + CHUNK_SIZE - 1;
      if (by + CHUNK_SIZE - 1 > maxY) maxY = by + CHUNK_SIZE - 1;
      if (bz + CHUNK_SIZE - 1 > maxZ) maxZ = bz + CHUNK_SIZE - 1;
      continue;
    }
    const cells = chunk.cells;
    for (let lx = 0; lx < CHUNK_SIZE; lx++)
      for (let ly = 0; ly < CHUNK_SIZE; ly++) {
        const rowBase = lx * CHUNK_AREA + ly * CHUNK_SIZE;
        for (let lz = 0; lz < CHUNK_SIZE; lz++) {
          if (cells[rowBase + lz] === 0) continue;
          const x = bx + lx, y = by + ly, z = bz + lz;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (z < minZ) minZ = z;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
          if (z > maxZ) maxZ = z;
        }
      }
  }
  if (minX === Infinity) return null;
  return { minX, minY, minZ, maxX, maxY, maxZ };
}

// ── Validation ─────────────────────────────────────────────────────────────
// Deep structural check so import + sync pulls reject hostile or corrupted
// payloads BEFORE they reach the decoders, which trust their input for speed
// (installRuns fills chunk storage from raw run lengths; bounds restore
// unclamped into the live work volume). Philosophy: REJECT data that would
// produce NaN geometry, absurd allocations, or crashes; stay LENIENT about
// missing optional fields the loaders already default (thumbnail, palette,
// reference, timestamps), so legitimate old documents keep loading.
// Dependency-free and linear in payload size — it runs on every load.

const isFin = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const isFinOrAbsent = (v: unknown): boolean => v === undefined || isFin(v);
const isStrOrAbsent = (v: unknown): boolean => v === undefined || typeof v === "string";
const isFinArray = (v: unknown, len: number): boolean =>
  Array.isArray(v) && v.length === len && v.every(isFin);

// Work-volume span ceiling (metres). Mirrors MAX_VOLUME_CELLS in
// scene/work-volume-bounds.ts (this module can't import upward from scene/).
// Interactive resize clamps to 256 cells per axis, so no legitimate document
// exceeds this; a crafted bounds beyond it would drive giant per-cell loops
// and allocations downstream. Small epsilon absorbs float drift in saves.
const MAX_BOUNDS_SPAN = 256 * CELL_SIZE + 1e-6;
// Bounds are gridGroup-local metres near the origin; anything this far out is junk.
const MAX_BOUNDS_POS = 1e6;
// A cell coordinate must map to a valid packed chunk key: chunk coords are
// biased 16-bit values, so |cell| < 32768 * CHUNK_SIZE.
const MAX_CELL_COORD = 32768 * CHUNK_SIZE;
// Packed chunk keys are (biased 16-bit)³ → [0, 2^48).
const MAX_CHUNK_KEY = 2 ** 48;

function isValidBounds(b: unknown): b is SavedModelCommon["bounds"] {
  if (!b || typeof b !== "object") return false;
  const { minX, maxX, minZ, maxZ, height } = b as Record<string, unknown>;
  return (
    isFin(minX) && isFin(maxX) && isFin(minZ) && isFin(maxZ) && isFin(height) &&
    minX <= maxX && minZ <= maxZ &&
    maxX - minX <= MAX_BOUNDS_SPAN && maxZ - minZ <= MAX_BOUNDS_SPAN &&
    height > 0 && height <= MAX_BOUNDS_SPAN &&
    Math.abs(minX) <= MAX_BOUNDS_POS && Math.abs(minZ) <= MAX_BOUNDS_POS
  );
}

function isValidScale(s: unknown): s is SavedModelCommon["scale"] {
  if (!s || typeof s !== "object") return false;
  const { inchesPerCell, unit } = s as Record<string, unknown>;
  // `unit` stays a plain string check so future units migrate, not reject.
  return isFin(inchesPerCell) && inchesPerCell > 0 && inchesPerCell <= 1e6 && typeof unit === "string";
}

function isValidRealIn(r: unknown): r is SavedModelCommon["realIn"] {
  if (!r || typeof r !== "object") return false;
  const { w, d, h } = r as Record<string, unknown>;
  return isFin(w) && w > 0 && isFin(d) && d > 0 && isFin(h) && h > 0 &&
    w <= 1e9 && d <= 1e9 && h <= 1e9;
}

function isValidReference(r: unknown): r is ReferenceData {
  if (!r || typeof r !== "object") return false;
  const ref = r as Record<string, unknown>;
  return (
    typeof ref.src === "string" &&
    isFin(ref.baseW) && ref.baseW > 0 &&
    isFin(ref.baseH) && ref.baseH > 0 &&
    isFinArray(ref.p, 3) && isFinArray(ref.q, 4) &&
    isFin(ref.s) && ref.s > 0 &&
    isFin(ref.opacity) &&
    (ref.framePos === undefined || isFinArray(ref.framePos, 3)) &&
    (ref.frameQuat === undefined || isFinArray(ref.frameQuat, 4))
  );
}

// One chunk's run-pairs starting at rle[at]: palette bytes, positive integer
// lengths, and the runs must tile the chunk EXACTLY (encoder invariant — a
// mismatch means corruption, and installRuns would install a chunk whose
// occupancy count disagrees with its cells).
function validRuns(rle: ArrayLike<number>, at: number, runs: number): boolean {
  let total = 0;
  let i = at;
  for (let r = 0; r < runs; r++) {
    const v = rle[i++];
    const len = rle[i++];
    if (!Number.isInteger(v) || v < 0 || v > 255) return false;
    if (!Number.isInteger(len) || len <= 0) return false;
    total += len;
  }
  return total === CHUNK_VOLUME;
}

// Flat v2 voxel stream: [key, runCount, value, count, …]* — every record
// complete (no truncation), every key a valid packed chunk key.
function isValidVoxelData(d: unknown[]): boolean {
  let i = 0;
  const n = d.length;
  while (i < n) {
    const key = d[i++];
    if (!Number.isInteger(key) || (key as number) < 0 || (key as number) >= MAX_CHUNK_KEY) return false;
    const runs = d[i++];
    if (!Number.isInteger(runs) || (runs as number) <= 0 || i + (runs as number) * 2 > n) return false;
    if (!validRuns(d as ArrayLike<number>, i, runs as number)) return false;
    i += (runs as number) * 2;
  }
  return true;
}

// Legacy per-chunk-object v2 voxels: [{key, rle}, …].
function isValidLegacyChunks(chunks: unknown[]): boolean {
  for (const c of chunks) {
    if (!c || typeof c !== "object") return false;
    const { key, rle } = c as Record<string, unknown>;
    if (!Number.isInteger(key) || (key as number) < 0 || (key as number) >= MAX_CHUNK_KEY) return false;
    if (!Array.isArray(rle) || rle.length === 0 || rle.length % 2 !== 0) return false;
    if (!validRuns(rle as ArrayLike<number>, 0, rle.length / 2)) return false;
  }
  return true;
}

// v1 dense cells: [ix, iy, iz, palette]*. cellsToGrid ignores a ragged tail,
// so we do too; every complete tuple must be integer coords in chunk-key range
// with a palette byte.
function isValidV1Cells(cells: unknown[]): boolean {
  for (let i = 0; i + 3 < cells.length; i += 4) {
    const x = cells[i];
    const y = cells[i + 1];
    const z = cells[i + 2];
    const p = cells[i + 3];
    if (!Number.isInteger(x) || Math.abs(x as number) >= MAX_CELL_COORD) return false;
    if (!Number.isInteger(y) || Math.abs(y as number) >= MAX_CELL_COORD) return false;
    if (!Number.isInteger(z) || Math.abs(z as number) >= MAX_CELL_COORD) return false;
    if (!Number.isInteger(p) || (p as number) < 0 || (p as number) > 255) return false;
  }
  return true;
}

// Structural + element-level check so import/sync rejects junk files. Accepts
// v1 (dense `cells`) and v2 (flat `voxels.data` OR legacy `voxels.chunks`).
export function isSavedModel(v: unknown): v is SavedModel {
  if (!v || typeof v !== "object") return false;
  const m = v as Record<string, unknown>;
  if (typeof m.id !== "string" || typeof m.name !== "string") return false;
  if (!isValidBounds(m.bounds) || !isValidScale(m.scale) || !isValidRealIn(m.realIn)) return false;
  // Optional-with-defaults fields: absent is fine; present-but-junk is not.
  if (!isStrOrAbsent(m.thumbnail)) return false;
  if (!isFinOrAbsent(m.createdAt) || !isFinOrAbsent(m.updatedAt)) return false;
  if (m.paletteColors !== undefined) {
    const pc = m.paletteColors;
    if (!Array.isArray(pc) || pc.length > 256 || !pc.every(isFin)) return false;
  }
  if (m.reference !== undefined && !isValidReference(m.reference)) return false;
  if (m.version === 2) {
    const vox = m.voxels as { data?: unknown; chunks?: unknown } | null | undefined;
    if (!vox || typeof vox !== "object") return false;
    if (Array.isArray(vox.data)) return isValidVoxelData(vox.data);
    if (Array.isArray(vox.chunks)) return isValidLegacyChunks(vox.chunks);
    return false;
  }
  return Array.isArray(m.cells) && isValidV1Cells(m.cells);
}
